import type { ServiceDetail } from "./types";

export const CORE_SERVICES_DATA: Record<string, ServiceDetail> = {
  "interior-design": {
    slug: "interior-design",
    title: "Interior Design",
    seoTitle: "Commercial Interior Design in Bahrain",
    seoDescription:
      "Abacus designs luxury retail, corporate, F&B and commercial interiors in Bahrain. 3D renders, space planning and MEP-coordinated shop drawings from an in-house studio.",
    category: "Core Service",
    subtitle:
      "Concept development and creative planning for retail, corporate, F&B, and luxury spaces.",
    description:
      "From concept boards to photorealistic 3D renders, our in-house design studio translates your brand identity into functional, buildable spaces - engineered for approval-ready execution from day one.",
    bullets: [
      "Photorealistic 3D Visualisation",
      "Value-Engineered Space Planning",
      "MEP-Coordinated Shop Drawings",
    ],
    imageAlt: "Commercial interior design studio 3D render Bahrain - Abacus",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/6e583bf9-3024-4034-8752-5085e01bffb7.webp",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/332813e8-8ebc-46a1-bf93-33cd038a3348.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/d5c11ecf-7a47-40fe-b328-cb77cbd01cab.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/e81afbec-f94b-4f27-b345-0f5805505f4d.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/3bdf9954-3684-4e20-81a9-2b05339e8efd.jpg",
    ],
    iconName: "PenTool",
    overview: [
      "Our interior design team turns initial sketches into immersive, functional brand environments. We specialize in luxury retail boutiques, corporate bank headquarters, fine dining restaurants, and high-end commercial spaces across Bahrain and the GCC.",
      "We bridge creative concept design with buildability, ensuring that every mood board, 3D visualization, material sample, and spatial layout translates flawlessly during fabrication and fit-out.",
    ],
    keyCapabilities: [
      {
        title: "Conceptual Spatial Planning",
        desc: "Optimizing flow, foot traffic, and customer engagement for maximum operational efficiency.",
      },
      {
        title: "3D Photorealistic Rendering",
        desc: "Detailed 3D visualizations giving you full clarity on material textures, lighting, and finishes before construction.",
      },
      {
        title: "Brand Identity Integration",
        desc: "Translating global luxury brand guidelines into bespoke physical interior experiences.",
      },
      {
        title: "Material & Finish Selection",
        desc: "Curating high-performance marbles, custom woods, brass accents, and acoustic wall coverings.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Discovery & Briefing",
        desc: "Understanding brand values, spatial requirements, site parameters, and operational goals.",
      },
      {
        step: "02",
        title: "Concept & Moodboards",
        desc: "Developing spatial layouts, material palettes, and initial 3D visualization studies.",
      },
      {
        step: "03",
        title: "Detailed Design & Engineering",
        desc: "Preparing full architectural drawings, MEP coordination blueprints, and shop drawings.",
      },
      {
        step: "04",
        title: "Handover to Production",
        desc: "Seamless alignment with our in-house joinery unit and site contracting teams.",
      },
    ],
    benefits: [
      {
        title: "24 Years Spatial Expertise",
        desc: "Trusted by 180+ international luxury brands across Bahrain's top shopping destinations.",
      },
      {
        title: "100% In-House Alignment",
        desc: "No design disconnects-our designers work under one roof with our manufacturing unit.",
      },
      {
        title: "Triple ISO Quality Assurance",
        desc: "ISO 9001 certified precision and attention to detail at every stage of the creative process.",
      },
    ],
    faqs: [
      {
        question: "Do you design for international brand compliance?",
        answer:
          "Yes, we regularly work with strict brand books from global luxury fashion houses like MaxMara, Hugo Boss, and Hackett London.",
      },
      {
        question: "Can you handle both design and full contracting?",
        answer:
          "Absolutely. Abacus provides true turnkey solutions from initial interior concept through to final key handover.",
      },
    ],
    relatedProjects: [
      {
        title: "MaxMara Boutique",
        category: "Luxury Retail",
        location: "Marassi Galleria, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/b30bbccc-6cf2-4d9f-9df9-3bbb6a88d81f.jpg",
        slug: "maxmara-boutique-marassi-galleria",
      },
      {
        title: "Al Salam Bank Al Raya Mall Branch",
        category: "Corporate",
        location: "Al Raya Mall at Riffa, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/e49f393e-5323-4f8a-b548-d78b8aa7058e.png",
        slug: "al-salam-bank-al-raya-mall-branch",
      },
    ],
  },

  "fit-out-contracting": {
    slug: "fit-out-contracting",
    title: "Fit-Out & Turnkey Contracting",
    seoTitle: "Turnkey Fit-Out Contracting in Bahrain",
    seoDescription:
      "Turnkey fit-out contracting in Bahrain with zero missed deadlines in 24 years. Civil works, MEP, joinery and handover under one accountable contract.",
    category: "Core Service",
    subtitle:
      "End-to-end delivery of commercial interiors with zero missed deadlines in 24 years.",
    description:
      "Complete turnkey delivery under one accountable contract - civil works, finishes, joinery installation and handover, executed to protect your opening date. Zero missed deadlines in 24 years.",
    bullets: [
      "Single-Contract Accountability",
      "Mall & Airport Airside Compliance",
      "Night-Shift & Fast-Track Execution",
    ],
    imageAlt: "Retail fit-out contractor Bahrain mall project - Abacus",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/51b95d9b-54b9-4392-bfb7-900e64123ad2.png",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/ae807b39-e7ea-4b85-afd6-6706bdd12009.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/912ffa3e-285d-462b-a5ff-654b48d0dbd8.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/4ee8f9c7-feb8-4ccb-bac0-a7945b25a204.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/82be80ad-1e47-4a16-8b92-36aaef750a28.jpg",
    ],
    iconName: "HardHat",
    overview: [
      "Our flagship turnkey fit-out service offers complete peace of mind. We manage demolition, civil works, MEP, custom joinery, ceilings, flooring, and airside security approvals for mall boutiques and corporate towers.",
      "With zero missed deadlines over 24 years, Abacus is the preferred contractor when opening dates are sacred and delay penalties are non-negotiable.",
    ],
    keyCapabilities: [
      {
        title: "Full Turnkey Project Management",
        desc: "Single point of contact overseeing site operations, authority permits, and quality controls.",
      },
      {
        title: "Mall & Airport Airside Compliance",
        desc: "Decades of experience navigating strict night-shift windows and security protocols at Marassi Galleria, City Centre, and Bahrain International Airport.",
      },
      {
        title: "Civil & Structural Engineering",
        desc: "Partitioning, raised floors, acoustic ceilings, and heavy structural reinforcements.",
      },
      {
        title: "Integrated Quality Inspection",
        desc: "Rigorous daily site audits certified under ISO 9001, ISO 14001, and ISO 45001.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Site Survey & Permits",
        desc: "Detailed site survey, authority submission approvals, and timeline scheduling.",
      },
      {
        step: "02",
        title: "Civil & MEP Infrastructure",
        desc: "Demolition, cabling, ducting, partitions, and structural preparation.",
      },
      {
        step: "03",
        title: "Joinery & Finishes",
        desc: "Installation of custom furniture, brass fixtures, wall cladding, and flooring.",
      },
      {
        step: "04",
        title: "Commissioning & Handover",
        desc: "Deep cleaning, testing, authority clearance, and on-time key handover.",
      },
    ],
    benefits: [
      {
        title: "Zero Missed Deadlines",
        desc: "A flawless 24-year track record across hundreds of high-stakes commercial handovers.",
      },
      {
        title: "In-House Factory Direct",
        desc: "Eliminating middleman delays through our 100% owned manufacturing facilities in Bahrain.",
      },
      {
        title: "Triple ISO Certified",
        desc: "Highest health, safety, environmental, and quality standards on every job site.",
      },
    ],
    faqs: [
      {
        question: "How do you handle mall night-shift fit-outs?",
        answer:
          "Our dedicated night crews operate under strict mall management guidelines without disrupting adjacent operating tenants.",
      },
      {
        question: "What authority approvals do you obtain?",
        answer:
          "We manage municipality, civil defense, electricity, and water authority clearances on your behalf.",
      },
    ],
    relatedProjects: [
      {
        title: "illy Café Lounge",
        category: "F&B",
        location: "City Centre, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/c9d4fce9-62f0-48c8-8dec-c769083f2da7.jpg?v=2",
        slug: "illy-cafe-city-centre",
      },
      {
        title: "Malabar Gold & Diamonds",
        category: "Luxury Retail",
        location: "Manama, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/7c8088f6-4bbe-4544-8bcf-829e2183acf7.png",
        slug: "malabar-gold-diamonds-showroom",
      },
    ],
  },

  "custom-joinery": {
    slug: "custom-joinery",
    title: "Joinery & Custom Furniture",
    seoTitle: "Custom Joinery & Furniture in Bahrain",
    seoDescription:
      "Custom joinery and furniture manufactured in-house in Bahrain. CNC woodwork, high-gloss spray booths and bespoke retail and hospitality fixtures.",
    category: "Core Service",
    subtitle:
      "State-of-the-art in-house manufacturing unit delivering custom woodwork to millimetre precision.",
    description:
      "Bespoke counters, wardrobes, display units and intricate wood panelling - designed, CNC-crafted and finished entirely in our own Bahrain facility, with no third-party dependencies.",
    bullets: [
      "Computerised CNC Wood Carving",
      "Dust-Free High-Gloss Spray Booths",
      "Bespoke Retail & Hospitality Fixtures",
    ],
    imageAlt: "Custom joinery workshop CNC wood carving Bahrain - Abacus",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/ce7f7ed8-b29d-416b-8ed7-e189bafa0be0.png",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/f7fc0581-b929-44ee-9f53-26ad4c9b0816.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/9d1b20be-eb57-41dd-9170-ab46063a8707.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/27846e83-0223-42ee-aa82-11c83a3004c0.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/c4f94aff-e0d0-412b-95d7-d4e08a32f39a.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/739a9bf1-80f4-4bba-becc-9c0ec173e95e.jpg",
    ],
    iconName: "Hammer",
    overview: [
      "Abacus operates one of the region's most advanced joinery and furniture production units in Road 5156, Askar, Kingdom of Bahrain. Equipped with modern CNC routers, precision edge-banders, and climate-controlled spray booths, we craft bespoke architectural woodwork.",
      "From high-gloss lacquer store counters to hand-veneered executive boardroom tables, our master carpenters ensure exquisite craftsmanship that endures heavy commercial traffic.",
    ],
    keyCapabilities: [
      {
        title: "Precision CNC Woodwork",
        desc: "Automated 3D carving, intricate grooving, and custom lattice screen production.",
      },
      {
        title: "Specialist Spray & Polish Booths",
        desc: "Flawless high-gloss, satin, matte, and metal-powder lacquer finishes.",
      },
      {
        title: "Luxury Retail Display Cabinets",
        desc: "Security glass integration, LED channel recessing, and lockable velvet jewelry trays.",
      },
      {
        title: "Architectural Wall Panelling & Ceilings",
        desc: "Acoustic timber baffles, curved veneer cladding, and solid wood moldings.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Shop Drawing Approval",
        desc: "Millimetre-accurate drafting detailing joinery connections, hardware, and finishes.",
      },
      {
        step: "02",
        title: "Timber Selection & CNC Milling",
        desc: "Selecting premium hardwoods, veneers, and precision automated machining.",
      },
      {
        step: "03",
        title: "Finishing & Assembly",
        desc: "In-house spray painting, hardware fitting, and pre-assembly quality control.",
      },
      {
        step: "04",
        title: "On-Site Installation",
        desc: "White-glove delivery and seamless site integration by master installation carpenters.",
      },
    ],
    benefits: [
      {
        title: "Total Quality Control",
        desc: "We don't rely on third-party carpentry shops-we own the machines and employ the artisans.",
      },
      {
        title: "Rapid Turnaround",
        desc: "Direct factory control allows fast-track production without compromising on finish.",
      },
      {
        title: "Custom Material Combinations",
        desc: "Expertly bonding timber with brass, stainless steel, corian, marble, and glass.",
      },
    ],
    faqs: [
      {
        question: "Where is your joinery workshop located?",
        answer:
          "Our facility is fully operational in Road 5156, Askar, Kingdom of Bahrain, serving projects across the country and the GCC.",
      },
      {
        question: "Can clients inspect production progress?",
        answer:
          "Yes! We encourage factory walkthroughs so clients can inspect raw mockups and finish samples in person.",
      },
    ],
    relatedProjects: [
      {
        title: "Hugo Boss Store Joinery",
        category: "Retail",
        location: "City Centre Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/90f42ad7-19bf-4199-9747-0877e401c069.png",
        slug: "hugo-boss-city-center",
      },
      {
        title: "illy Barista Counter",
        category: "F&B",
        location: "Marassi Galleria, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/c9d4fce9-62f0-48c8-8dec-c769083f2da7.jpg?v=2",
        slug: "illy-cafe-city-centre",
      },
    ],
  },

  "mep-contracting": {
    slug: "mep-contracting",
    title: "MEP Solutions",
    seoTitle: "MEP Contracting for Commercial Interiors",
    seoDescription:
      "MEP contracting in Bahrain: HVAC, electrical, plumbing and fire safety fully integrated with interior design to eliminate on-site conflicts and delays.",
    category: "Core Service",
    subtitle:
      "Fully integrated Mechanical, Electrical, Plumbing, and HVAC engineering for commercial interiors.",
    description:
      "Mechanical, electrical and plumbing engineering fully integrated with the interior design - HVAC, power, lighting circuits and plumbing coordinated in-house to eliminate on-site conflicts and delays.",
    bullets: [
      "Integrated HVAC & Electrical Design",
      "Authority Approvals & Compliance",
    ],
    imageAlt:
      "MEP engineering electrical installation and distribution board by Abacus Bahrain",
    heroImage:
      "https://vibe.filesafe.space/1785525013889768799/attachments/8fb923f7-8b31-4a1d-83c9-257ad3f85a16.jpg",
    gallery: [
      "https://vibe.filesafe.space/1785525013889768799/attachments/66b302c8-67c2-417e-b3b0-d9ad54fa4284.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/30e7badf-79af-42dd-acca-14e39e51b620.jpg",
      "https://vibe.filesafe.space/1785525013889768799/attachments/de14617a-b8a9-4d1a-9955-acbd9a11dedd.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/6e49d989-2f38-4308-9c5e-b7cacbf8109b.png",
      "https://vibe.filesafe.space/1785525013889768799/attachments/274bb540-2395-4cd7-8389-c8df88356d02.jpg",
    ],
    iconName: "Zap",
    overview: [
      "A stunning interior is only as good as the infrastructure supporting it. Our specialized MEP division provides complete mechanical, electrical, plumbing, and fire safety systems tailored for demanding commercial spaces.",
      "We seamlessly integrate air conditioning ducts, power distribution boards, smart lighting controls, and fire suppression systems into the architectural aesthetic, eliminating visible clutter.",
    ],
    keyCapabilities: [
      {
        title: "HVAC & Thermal Comfort",
        desc: "Precision duct design, chilled water connections, VRF systems, and acoustic silencers.",
      },
      {
        title: "Electrical & Data Infrastructure",
        desc: "Main distribution boards, structured cabling, UPS systems, and architectural lighting loads.",
      },
      {
        title: "Plumbing & Drainage Engineering",
        desc: "Hot/cold water distribution, grease traps for F&B outlets, and luxury sanitary fittings.",
      },
      {
        title: "Fire Alarm & Suppression Compliance",
        desc: "Smoke detectors, sprinkler networks, and Civil Defence authority sign-offs.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Heat & Load Calculations",
        desc: "Calculating electrical kW requirements, cooling CFM, and water flow capacities.",
      },
      {
        step: "02",
        title: "Authority Blueprints",
        desc: "Submitting detailed MEP drawings to EWA, Civil Defence, and Mall Management.",
      },
      {
        step: "03",
        title: "First-Fix Infrastructure",
        desc: "Concealed containment, duct routing, piping, and distribution board setup.",
      },
      {
        step: "04",
        title: "Testing & Commissioning",
        desc: "Balancing airflows, thermal imaging, pressure testing, and final certification.",
      },
    ],
    benefits: [
      {
        title: "Zero Interference Design",
        desc: "MEP engineers work directly with interior designers to avoid ceiling drop or bulk conflict.",
      },
      {
        title: "Energy Efficient Systems",
        desc: "Implementing smart inverter technology and LED power optimization.",
      },
      {
        title: "Authority Approval Guarantee",
        desc: "Complete compliance with Kingdom of Bahrain building codes and standards.",
      },
    ],
    faqs: [
      {
        question: "Do you handle kitchen MEP for F&B outlets?",
        answer:
          "Yes, including commercial kitchen exhaust, ecology units, gas detectors, and grease traps.",
      },
      {
        question:
          "How do you ensure quiet air conditioning in executive offices?",
        answer:
          "We install inline sound attenuators, insulated flexible ducting, and low-NC diffusers.",
      },
    ],
    relatedProjects: [
      {
        title: "Al Salam Bank Branch MEP",
        category: "Corporate",
        location: "Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/e49f393e-5323-4f8a-b548-d78b8aa7058e.png",
        slug: "al-salam-bank-al-raya-mall-branch",
      },
      {
        title: "City Centre F&B Infrastructure",
        category: "Hospitality",
        location: "Manama, Bahrain",
        image:
          "https://vibe.filesafe.space/1785525013889768799/attachments/c9d4fce9-62f0-48c8-8dec-c769083f2da7.jpg?v=2",
        slug: "illy-cafe-city-centre",
      },
    ],
  },
};
